import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Button } from "~/components/button";
import { Modal } from "~/components/modal/modal";
import { Label } from "~/components/ui/label";
import { Input } from "~/components/ui/input";
import { useDialog } from "~/hooks/use-dialog";
import { toast } from "~/utils/toast";
import { useOrganisations } from "~/contexts/organisations-provider";

interface FormValues {
  name: string;
}

export const AddOrganisationModal = () => {
  const { t } = useTranslation(["settings"]);

  const { addOrganisation } = useOrganisations();

  const dialog = useDialog("add-organisation");

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
    },
  });

  async function onCreate(values: FormValues) {
    try {
      await addOrganisation({ name: values.name });

      toast.success(
        t("settings:organisations.overview.add.success", {
          name: values.name,
        }),
      );

      form.reset();
      dialog.close();
    } catch (error) {
      toast.error(
        t("settings:organisations.overview.add.error", {
          message: (error as Error).message,
        }),
      );
    }
  }

  const onSubmit = form.handleSubmit(onCreate);

  return (
    <Modal
      id="add-organisation"
      open={dialog.isOpen}
      onOpenChange={dialog.onOpenChange}
      variant="primary"
      mainIconName="building"
      title={t("settings:organisations.overview.add.modal.title")}
      description={t("settings:organisations.overview.add.modal.description")}
      confirmLabel={t("settings:organisations.overview.add.modal.actions.add")}
      confirmIconName="plus"
      cancelLabel={t(
        "settings:organisations.overview.add.modal.actions.cancel",
      )}
      onConfirm={onSubmit}
      disabledConfirm={!form.formState.isValid}
      trigger={
        <Button
          label={t("settings:organisations.overview.add.button")}
          icon="plus"
          className="py-2 font-normal"
        />
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex flex-col gap-3">
          <Label htmlFor="org-name">
            {t("settings:organisations.overview.add.modal.name.label")}
          </Label>
          <Input
            id="org-name"
            className="placeholder:text-secondary-400 rounded-lg placeholder:text-sm text-sm"
            {...form.register("name", {
              required: true,
              setValueAs: (v) => v.trim(),
            })}
          />
        </div>
      </form>
    </Modal>
  );
};
